/**
 * Modal that pauses the pipeline at the STL or LSTM threshold step.
 *
 * Markus's spec (Python L1256, L1400): the relevant intermediate plot must be
 * visible BEFORE the user enters a threshold; backend validation errors
 * (L1260-1271, L1404-1415) must surface inline without closing the modal.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PlotData } from '../types';
import AnomalyChart from './AnomalyChart';
import AnomalyOverlayChart from './AnomalyOverlayChart';

// Keys must match the `component` strings emitted by the backend
// (plot_builder.py:157-162): "observed" | "trend" | "seasonal" | "resid".
const STL_COLOR_MAP: Record<string, string> = {
  observed: '#14B8A6',
  trend: '#16a34a',
  seasonal: '#a855f7',
  resid: '#ef4444',
};

// Compact height per chart in the STL modal so all 4 decomposition plots
// fit inside the 90vh modal without scrolling. LSTM (single chart) keeps
// the default height.
const STL_CHART_HEIGHT = 170;

export interface ThresholdInputModalProps {
  /** "stl" or "lstm" — drives the title only. */
  variant: 'stl' | 'lstm';
  /** Plot(s) to render before the input. STL has 4 sub-plots; LSTM has 1. */
  plots: PlotData[];
  /** Submitting calls this with the parsed numeric value. */
  onSubmit: (threshold: number) => void;
  onCancel: () => void;
  /** Last error from server-side validation; modal stays open until cleared. */
  serverError: string | null;
  /** True while the threshold POST is in flight; disables submit. */
  submitting: boolean;
  /**
   * Optional informational notice shown above the plots (not an error). Used by
   * the STL variant to surface backend-localized coverage warnings (e.g. how
   * many segments were too short to scan) while the user picks the threshold.
   */
  notice?: string | null;
}

const ThresholdInputModal: React.FC<ThresholdInputModalProps> = ({
  variant,
  plots,
  onSubmit,
  onCancel,
  serverError,
  submitting,
  notice,
}) => {
  const { t } = useTranslation('dataProcessing');
  const [raw, setRaw] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  // Shared brush range — drives synchronized zoom/pan across all sub-plots
  // (mostly relevant for the 4 STL decomposition charts; LSTM modal renders
  // only one plot so the sync is a no-op there).
  const [sharedBrushRange, setSharedBrushRange] = useState<
    { startTime: number; endTime: number } | null
  >(null);

  // Clear local error when server replies (so user sees server message instead).
  useEffect(() => {
    if (serverError) setLocalError(null);
  }, [serverError]);

  // Lock page scroll while the modal is open so wheel events over the
  // backdrop (or anywhere else outside the modal) don't move the page
  // underneath. Pattern mirrors ViolinPlotsModal / PlottingModal.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const label = t('anomaly.modal.thresholdLabel');

  /**
   * Validate the raw input string. Returns an error message or null.
   * Pure — called both on every keystroke (live UX feedback) and inside
   * handleSubmit (defensive, in case caller skips onChange).
   *
   * Hardened against JS `Number()` quirks discovered via chaos testing:
   *   "1,000"  → would be Number("1.000") = 1   (DE-locale ambiguous, EN typo)
   *   "1e10"   → would be Number("1e10") = 1e10 (scientific notation)
   *   "0x10"   → would be Number("0x10") = 16   (hex literal)
   *
   * Accepts only:
   *   - optional sign:           [+-]?
   *   - either:
   *       digits-only int:        \d+
   *       int + decimal:          \d+[.,]\d+
   *       leading-decimal:        [.,]\d+
   * Comma is the DE/CH decimal separator; period is also accepted.
   * Multiple separators, scientific notation, hex literals all reject.
   */
  // Period decimal (\.\d+): any number of digits — accepts any precision.
  // Comma decimal (\,\d{1,2}): capped at 1-2 digits. This intentionally
  // rejects "1,000" and "5,123" alike — not because of a three-digit rule,
  // but because anything beyond 2 digits exceeds the cap. For this domain
  // (anomaly thresholds, typically 1-100 with 0-2 decimal places) 2 digits
  // is sufficient; "5,5" and "10,25" (DE/CH decimal notation) both pass.
  const NUMERIC_RE = /^[+-]?(\d+(\.\d+|,\d{1,2})?|[.,]\d+)$/;

  const validateRaw = (rawValue: string): string | null => {
    if (rawValue.trim() === '') {
      return null; // empty = waiting for user, not an error yet
    }
    const trimmed = rawValue.trim();
    if (!NUMERIC_RE.test(trimmed)) {
      return t('anomaly.modal.errors.invalid', { label });
    }
    const v = Number(trimmed.replace(',', '.'));
    if (!Number.isFinite(v)) {
      // Defensive — regex already rejected non-finite, but belt + braces.
      return t('anomaly.modal.errors.invalid', { label });
    }
    if (v < 0) {
      return t('anomaly.modal.errors.negative', { label });
    }
    return null;
  };

  const handleSubmit = () => {
    if (raw.trim() === '') {
      setLocalError(t('anomaly.modal.errors.required', { label }));
      return;
    }
    const err = validateRaw(raw);
    if (err) {
      setLocalError(err);
      return;
    }
    // Accept comma as decimal separator (DE/CH).
    const v = Number(raw.replace(',', '.'));
    setLocalError(null);
    onSubmit(v);
  };

  // Escape closes the modal — keyboard accessibility.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const title = variant === 'stl'
    ? t('anomaly.modal.stlTitle')
    : t('anomaly.modal.lstmTitle');

  // STL packs 4 charts into one modal — squeeze surrounding margins so the
  // grid fits in 90vh without scrolling. LSTM (single chart) keeps roomy
  // defaults.
  const isStl = variant === 'stl';
  const modalPadCls = isStl ? 'p-4' : 'p-6';
  const gridWrapperCls = isStl ? 'mt-2 space-y-1' : 'mt-4 space-y-3';
  const inputSectionCls = isStl ? 'mt-3' : 'mt-6';
  const actionsCls = isStl ? 'mt-3 flex justify-end gap-3' : 'mt-6 flex justify-end gap-3';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="threshold-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
    >
      <div className={`w-full max-w-4xl max-h-[90vh] overflow-auto overscroll-contain rounded-lg bg-card shadow-xl ${modalPadCls}`}>
        <div className="flex items-center justify-between gap-3">
          <h3 id="threshold-modal-title" className="text-lg font-semibold text-foreground">
            {title}
          </h3>
          <button
            type="button"
            onClick={() => setSharedBrushRange(null)}
            className="shrink-0 rounded-md border border-border px-3 py-1 text-xs text-foreground transition-colors hover:bg-muted"
            aria-label={t('charts:dataChart.resetZoom', 'Reset zoom')}
          >
            {t('charts:dataChart.resetZoom', 'Reset zoom')}
          </button>
        </div>

        {notice ? (
          <div
            role="status"
            data-testid="stl-coverage-warning"
            className="mt-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
          >
            {notice}
          </div>
        ) : null}

        <div className={gridWrapperCls}>
          {plots.map((p, i) =>
            p.type === 'multi'
              ? <AnomalyOverlayChart
                  key={i}
                  plot={p}
                  height={variant === 'stl' ? STL_CHART_HEIGHT : undefined}
                  hideControls
                  sharedBrushRange={sharedBrushRange}
                  onSharedBrushChange={setSharedBrushRange}
                />
              : <AnomalyChart
                  key={i}
                  plot={p}
                  hideControls
                  color={p.component ? STL_COLOR_MAP[p.component] : undefined}
                  height={variant === 'stl' ? STL_CHART_HEIGHT : undefined}
                  sharedBrushRange={sharedBrushRange}
                  onSharedBrushChange={setSharedBrushRange}
                />
          )}
        </div>

        <div className={inputSectionCls}>
          <label
            htmlFor="threshold-input"
            className="block text-sm font-medium text-foreground"
          >
            {t('anomaly.modal.thresholdLabel')}
          </label>
          <input
            id="threshold-input"
            // type=text + inputmode=decimal so DE users can enter "3,5"
            // (type=number rejects comma in many browsers).
            type="text"
            inputMode="decimal"
            value={raw}
            onChange={(e) => {
              const next = e.target.value;
              setRaw(next);
              setLocalError(validateRaw(next));
            }}
            data-testid="threshold-input"
            className={
              'mt-1 w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring-2 ' +
              (serverError || localError
                ? 'border-red-500 focus:ring-red-300'
                : 'border-input focus:ring-ring')
            }
            disabled={submitting}
            aria-invalid={!!(serverError || localError)}
            aria-describedby={serverError || localError ? 'threshold-error' : undefined}
          />
          {(serverError || localError) && (
            <p id="threshold-error" data-testid="threshold-error"
               aria-live="polite"
               className="mt-1 text-xs text-red-600">
              {serverError ?? localError}
            </p>
          )}
        </div>

        <div className={actionsCls}>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            data-testid="threshold-cancel"
            className="rounded border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
          >
            {t('anomaly.actions.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || raw.trim() === '' || validateRaw(raw) !== null}
            data-testid="threshold-submit"
            className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {submitting ? '…' : t('anomaly.actions.submitThreshold')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThresholdInputModal;
